// views/alert/AlertListView.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLoading } from "@/store/loadingStore";
import { useModal } from "@/store/modalStore";
import { 
  getAlertLogsApi, 
  getAlertStatsApi, 
  updateAlertStatusApi, 
  exportAlertLogsApi,
  AlertLogResponse,
  AlertLogStats 
} from "@/lib/api/alertLog";
import "./AlertListView.css";

// AlertLogResponse를 바로 사용하므로 AlertData 인터페이스는 제거
// interface AlertData는 AlertLogResponse로 대체됨

const AlertListView = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();
  const modal = useModal();

  // 상태 관리
  const [alerts, setAlerts] = useState<AlertLogResponse[]>([]);
  const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
  const [province, setProvince] = useState("전라남도");
  const [city, setCity] = useState("나주시");
  const [district, setDistrict] = useState(""); // 지역 선택을 위해 초기값을 빈 문자열로
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]); // 오늘 날짜
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);

  // 통계 정보
  const [stats, setStats] = useState<AlertLogStats>({
    alarm: 0,
    notification: 0,
    processed: 0,
  });

  // 선택된 알림 정보
  const [selectedAlert, setSelectedAlert] = useState<AlertLogResponse | null>(null);

  // 지역 목록 - 나주시 하위 지역들
  const districts = [
    "빛가람동", "노안면", "다도면", "다시면", "반남면", 
    "공산면", "동강면", "문평면", "금천면", "산포면", 
    "봉황면", "세지면", "왕곡면", "송월동", "영강동", 
    "금남동", "성북동", "영산동", "이창동"
  ];

  // 데이터 로드
  useEffect(() => {
    loadAlerts();
  }, [currentPage, filterDate]);

  const loadAlerts = async () => {
    try {
      startLoading("알림 목록 로딩 중...");
      
      // 알림 로그 목록 조회
      const response = await getAlertLogsApi({
        page: currentPage,
        size: pageSize,
        regionName: district || undefined,
        filterDate: filterDate || undefined,
        sortBy: "createdAt",
        sortDir: "desc"
      });
      
      setAlerts(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
      
      // 통계 조회
      await loadStats();
      
    } catch (error: any) {
      console.error("알림 목록 로딩 실패:", error);
      modal.alert("오류", error.message || "알림 목록을 불러오는데 실패했습니다.");
    } finally {
      stopLoading();
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await getAlertStatsApi(filterDate || undefined);
      setStats(statsData);
    } catch (error: any) {
      console.error("통계 로딩 실패:", error);
      // 통계 로딩 실패는 사용자에게 알리지 않고 기본값 유지
    }
  };

  // 검색
  const handleSearch = async () => {
    loadAlerts();
  };

  // 행 클릭시 상세 정보 표시
  const handleRowClick = (alert: AlertLogResponse) => {
    setSelectedAlert(alert);
  };

  // 엑셀 다운로드
  const handleExcelDownload = async () => {
    try {
      startLoading("엑셀 파일 생성 중...");
      await exportAlertLogsApi({
        regionName: district || undefined,
        filterDate: filterDate || undefined
      });
      modal.alert("완료", "엑셀 파일이 다운로드되었습니다.");
    } catch (error: any) {
      console.error("엑셀 다운로드 실패:", error);
      modal.alert("오류", error.message || "엑셀 다운로드 중 오류가 발생했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 처리완료 처리
  const handleProcess = async () => {
    if (!selectedAlert) return;
    
    try {
      startLoading("처리 중...");
      await updateAlertStatusApi(selectedAlert.id, "관리자가 수동으로 처리 완료");
      modal.alert("완료", "처리가 완료되었습니다.");
      
      // 목록 새로고침
      await loadAlerts();
      
      // 선택된 알림 상태 업데이트
      setSelectedAlert(prev => prev ? { ...prev, status: "성공" } : null);
      
    } catch (error: any) {
      console.error("처리 실패:", error);
      modal.alert("오류", error.message || "처리 중 오류가 발생했습니다.");
    } finally {
      stopLoading();
    }
  };

  // 문자 발송 (실제 전화번호가 있으면 전화 걸기)
  const handleSendSMS = () => {
    if (selectedAlert) {
      // 실제 관리자 연락처는 AlertAdmin에서 가져와야 함
      // 임시로 더미 번호 사용
      window.open("tel:010-4822-3645");
    }
  };

  return (
    <div className="alert-list-container">
      {/* 탭 버튼 */}
      <div className="tab-buttons">
        <button
          className="tab-button active"
          onClick={() => {
            window.location.href = "/alert";
          }}
        >
          나주시 경고알림 현황
        </button>
        <button
          className="tab-button"
          onClick={() => {
            window.location.href = "/alert/admin";
          }}
        >
          경고알림 관리자
        </button>
        <button
          className="tab-button"
          onClick={() => {
            window.open("https://gabia.com", "_blank");
          }}
        >
          문자충전
        </button>
        <a href="#" className="btn-excel-download" onClick={handleExcelDownload}>
          <img src="/img/btn_excel01.png" alt="엑셀 다운로드" />
        </a>
      </div>

      <div className="alv_dv01">
        {/* 검색 영역 */}
        <div className="search-top">
          <div className="search-section">
            <select className="search-type" disabled style={{color: '#666', backgroundColor: '#f5f5f5'}}>
              <option value="전라남도">전라남도</option>
            </select>
            <select className="search-type" disabled style={{color: '#666', backgroundColor: '#f5f5f5'}}>
              <option value="나주시">나주시</option>
            </select>
            <select 
              className="search-type" 
              value={district} 
              onChange={(e) => setDistrict(e.target.value)}
            >
              <option value="">전체 지역</option>
              {districts.map((dist) => (
                <option key={dist} value={dist}>{dist}</option>
              ))}
            </select>
            <span className="date-label">날짜</span>
            <input
              type="date"
              className="date-input"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
            <button className="btn-search" onClick={handleSearch}>
              조회
            </button>
          </div>

          {/* 통계 정보 */}
          <div className="stats-section">
            <span className="stat-item alarm">
              <span className="icon">🔔</span> {stats.alarm}
            </span>
            <span className="stat-item">
              대기 <strong className="text-blue">{stats.notification}</strong>
            </span>
            <span className="stat-item">
              처리 <strong className="text-gray">{stats.processed}</strong>
            </span>
          </div>
        </div>

        {/* 테이블 */}
        <div className="table-section">
          <table className="alert-table">
            <thead>
              <tr>
                <th>경보일자</th>
                <th>지역</th>
                <th>측정지점</th>
                <th>등급</th>
                <th>복합악취</th>
                <th>황화수소(ppm)</th>
                <th>암모니아(ppm)</th>
                <th>메탄가스(ppm)</th>
                <th>담당자</th>
                <th>담당자연락처</th>
                <th>처리현황</th>
                <th className="arrow-header">▲</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr
                  key={alert.id}
                  className={selectedAlert?.id === alert.id ? "selected" : ""}
                  onClick={() => handleRowClick(alert)}
                >
                  <td>{new Date(alert.createdAt).toLocaleString("ko-KR")}</td>
                  <td>{alert.regionName}</td>
                  <td>{alert.deviceName || "기기명 미확인"}</td>
                  <td>
                    <span className="event-type very-bad">{alert.grade}</span>
                  </td>
                  <td>{alert.h2sValue ? `${alert.h2sValue}` : "-"}</td>
                  <td>{alert.nh3Value ? `${alert.nh3Value}` : "-"}</td>
                  <td>{alert.nh3Value ? `${alert.nh3Value}` : "-"}</td>
                  <td>-</td> {/* 메탄가스는 현재 데이터에 없음 */}
                  <td>관리자 ({alert.adminCount}명)</td>
                  <td>연락처 미표시</td> {/* 보안상 연락처 숨김 */}
                  <td>
                    <span className={`status ${
                      alert.status === "대기중" || alert.status === "발송중" ? "pending" : "completed"
                    }`}>
                      {alert.status}
                    </span>
                  </td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 선택된 알림 상세 정보 */}
      {selectedAlert && (
        <div className="alert-detail-section">
          <div className="detail-left">
            <div className="detail-header">
              <h3>{selectedAlert.deviceName || "기기명 미확인"}</h3>
              <button className="btn-process" onClick={handleProcess}>
                처리완료
              </button>
            </div>

            <div className="detail-info">
              <table className="detail-table">
                <tbody>
                  <tr>
                    <td className="label">지역</td>
                    <td className="value" colSpan={3}>
                      전라남도 나주시 {selectedAlert.regionName}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">등급</td>
                    <td className="value">{selectedAlert.grade}</td>
                  </tr>
                  <tr>
                    <td className="label">황화수소</td>
                    <td className="value">
                      {selectedAlert.h2sValue ? `${selectedAlert.h2sValue} ppm` : "측정불가"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">암모니아</td>
                    <td className="value">
                      {selectedAlert.nh3Value ? `${selectedAlert.nh3Value} ppm` : "측정불가"}
                    </td>
                  </tr>
                  <tr>
                    <td className="label">발송 상태</td>
                    <td className="value">
                      성공 {selectedAlert.successCount}명 / 실패 {selectedAlert.failCount}명
                      <button className="btn-sms" onClick={handleSendSMS}>
                        문자발송 📱
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td className="label">발송 시간</td>
                    <td className="value">
                      {selectedAlert.sentAt ? 
                        new Date(selectedAlert.sentAt).toLocaleString("ko-KR") : 
                        "미발송"
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="detail-center">
            <h3>알림 메시지</h3>
            <div className="photo-container" style={{padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px'}}>
              <pre style={{fontSize: '12px', lineHeight: '1.4', whiteSpace: 'pre-wrap'}}>
                {selectedAlert.message || "메시지 없음"}
              </pre>
            </div>
          </div>

          <div className="detail-right">
            <div className="detail-header">
              <h3>처리현황</h3>
              <button className="btn-save">저장</button>
            </div>
            <div className="process-content">
              <textarea
                className="process-textarea"
                defaultValue={
                  selectedAlert.errorMessage || 
                  `${new Date(selectedAlert.createdAt).toLocaleString("ko-KR")}에 알림이 발송되었습니다. 관리자 확인 필요.`
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertListView;
